/**
 * Message Constants
 * Centralized user-facing messages for errors, success states, and loading states
 */

export const ERROR_MESSAGES = {
  // Network errors
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
  REQUEST_FAILED: 'Request failed. Please try again.',
  TIMEOUT: 'Request timed out. Please try again.',

  // Movie errors
  MOVIE_NOT_FOUND: 'Movie not found.',
  MOVIES_LOAD_FAILED: 'Failed to load movies. Please try again.',
  MOVIE_DETAILS_FAILED: 'Could not load movie details.',
  SIMILAR_MOVIES_FAILED: 'Could not load similar movies.',
  TRAILER_NOT_FOUND: 'Trailer not available.',
  TRAILER_LOAD_FAILED: 'Could not load trailer.',
  CAST_LOAD_FAILED: 'Could not load cast information.',
  PROVIDERS_LOAD_FAILED: 'Could not load streaming providers.',
  POSTERS_LOAD_FAILED: 'Could not load movie posters.',

  // Actor errors
  ACTOR_NOT_FOUND: 'Actor not found.',
  ACTOR_DETAILS_FAILED: 'Could not load actor details.',
  ACTOR_MOVIES_FAILED: 'Could not load actor filmography.',

  // Library errors
  ADD_TO_LIBRARY_FAILED: 'Failed to add movie to library.',
  REMOVE_FROM_LIBRARY_FAILED: 'Failed to remove movie from library.',
  LIBRARY_LOAD_FAILED: 'Failed to load your library.',
  LIBRARY_SYNC_FAILED: 'Failed to sync library.',

  // Category errors
  CATEGORY_CREATE_FAILED: 'Failed to create category.',
  CATEGORY_DELETE_FAILED: 'Failed to delete category.',
  CATEGORY_EXISTS: 'A category with this name already exists.',
  CATEGORY_NAME_REQUIRED: 'Category name is required.',
  ADD_TO_CATEGORY_FAILED: 'Failed to add movie to category.',
  REMOVE_FROM_CATEGORY_FAILED: 'Failed to remove movie from category.',

  // Authentication errors
  AUTH_FAILED: 'Authentication failed.',
  LOGIN_FAILED: 'Login failed. Please check your credentials.',
  SIGNUP_FAILED: 'Signup failed. Please try again.',
  LOGOUT_FAILED: 'Logout failed.',
  SESSION_EXPIRED: 'Your session has expired. Please log in again.',
  UNAUTHORIZED: 'You must be logged in to perform this action.',

  // Search errors
  SEARCH_FAILED: 'Search failed. Please try again.',
  SEARCH_QUERY_REQUIRED: 'Please enter a search query.',

  // Recommendation errors
  RECOMMENDATIONS_FAILED: 'Failed to generate recommendations.',
  AI_SERVICE_ERROR: 'AI service temporarily unavailable.',

  // General errors
  UNKNOWN_ERROR: 'An unexpected error occurred.',
  LOAD_FAILED: 'Failed to load data. Please try again.',
  ACTION_FAILED: 'Action failed. Please try again.',
};

export const SUCCESS_MESSAGES = {
  // Library success
  ADDED_TO_LIBRARY: 'Added to your library!',
  REMOVED_FROM_LIBRARY: 'Removed from your library.',
  LIBRARY_SYNCED: 'Library synced successfully.',

  // Category success
  CATEGORY_CREATED: 'Category created successfully!',
  CATEGORY_DELETED: 'Category deleted successfully.',
  ADDED_TO_CATEGORY: 'Movie added to category!',
  REMOVED_FROM_CATEGORY: 'Movie removed from category.',

  // Authentication success
  LOGIN_SUCCESS: 'Logged in successfully!',
  SIGNUP_SUCCESS: 'Account created successfully!',
  LOGOUT_SUCCESS: 'Logged out successfully.',

  // General success
  ACTION_SUCCESS: 'Action completed successfully!',
  SAVED: 'Saved successfully.',
};

export const LOADING_MESSAGES = {
  // General loading
  LOADING: 'Loading...',
  PLEASE_WAIT: 'Please wait...',

  // Specific loading states
  LOADING_MOVIES: 'Loading movies...',
  LOADING_MOVIE_DETAILS: 'Loading movie details...',
  LOADING_ACTORS: 'Loading actors...',
  LOADING_ACTOR_DETAILS: 'Loading actor details...',
  LOADING_TRAILER: 'Loading trailer...',
  LOADING_CAST: 'Loading cast...',
  LOADING_SIMILAR: 'Loading similar movies...',
  LOADING_PROVIDERS: 'Loading streaming providers...',
  LOADING_POSTERS: 'Loading posters...',
  LOADING_LIBRARY: 'Loading your library...',
  LOADING_CATEGORIES: 'Loading categories...',
  LOADING_RECOMMENDATIONS: 'Generating recommendations...',

  // Action loading
  ADDING_TO_LIBRARY: 'Adding to library...',
  REMOVING_FROM_LIBRARY: 'Removing from library...',
  CREATING_CATEGORY: 'Creating category...',
  DELETING_CATEGORY: 'Deleting category...',
  SEARCHING: 'Searching...',
  LOGGING_IN: 'Logging in...',
  SIGNING_UP: 'Creating account...',
};

export const INFO_MESSAGES = {
  // Empty states
  NO_RESULTS: 'No results found.',
  NO_MOVIES: 'No movies found.',
  NO_ACTORS: 'No actors found.',
  LIBRARY_EMPTY: 'Your library is empty. Start adding movies!',
  NO_CATEGORIES: 'No categories yet. Create your first category!',
  NO_SIMILAR_MOVIES: 'No similar movies found.',
  NO_TRAILER: 'No trailer available.',
  NO_CAST: 'Cast information not available.',
  NO_PROVIDERS: 'Streaming information not available.',
  NO_BIOGRAPHY: 'Biography not available.',

  // Instructions
  SEARCH_INSTRUCTION: 'Search for movies by title, actor, or keyword.',
  SELECT_GENRE: 'Select a genre to filter movies.',
  LOGIN_REQUIRED: 'Please log in to use this feature.',
  CREATE_ACCOUNT: "Don't have an account? Sign up now!",

  // Limits
  RATE_LIMIT: 'Please wait a moment before trying again.',
  MAX_RESULTS: 'Showing maximum results. Refine your search for better results.',
};

export const PLACEHOLDER_TEXT = {
  SEARCH: 'Search for movies...',
  EMAIL: 'Email',
  PASSWORD: 'Password',
  CATEGORY_NAME: 'Enter category name',
  CONFIRM_PASSWORD: 'Confirm password',
};

export const BUTTON_TEXT = {
  // Actions
  SEARCH: 'Search',
  LOAD_MORE: 'Load More',
  BACK: 'Back',
  CLOSE: 'Close',
  CANCEL: 'Cancel',
  CONFIRM: 'Confirm',
  SAVE: 'Save',
  DELETE: 'Delete',
  EDIT: 'Edit',
  RETRY: 'Retry',

  // Library actions
  ADD_TO_LIBRARY: 'Add to Library',
  REMOVE_FROM_LIBRARY: 'Remove from Library',
  IN_LIBRARY: 'In Library',
  ADD_TO_CATEGORY: 'Add to Category',
  CREATE_CATEGORY: 'Create Category',

  // Authentication
  LOGIN: 'Log In',
  SIGNUP: 'Sign Up',
  LOGOUT: 'Log Out',
  SIGN_IN: 'Sign In',
  CREATE_ACCOUNT: 'Create Account',

  // Navigation
  HOME: 'Home',
  LIBRARY: 'Library',
  BROWSE: 'Browse',
  DISCOVER: 'Discover',

  // Media
  WATCH_TRAILER: 'Watch Trailer',
  VIEW_DETAILS: 'View Details',
  VIEW_CAST: 'View Cast',
  VIEW_SIMILAR: 'View Similar',
};

export const ARIA_LABELS = {
  // Navigation
  MAIN_NAVIGATION: 'Main navigation',
  MOBILE_MENU: 'Mobile menu',
  CLOSE_MENU: 'Close menu',
  OPEN_MENU: 'Open menu',

  // Actions
  ADD_TO_LIBRARY: 'Add to library',
  REMOVE_FROM_LIBRARY: 'Remove from library',
  SEARCH_MOVIES: 'Search movies',
  FILTER_BY_GENRE: 'Filter by genre',
  LOAD_MORE_MOVIES: 'Load more movies',

  // Media
  MOVIE_POSTER: 'Movie poster',
  ACTOR_PHOTO: 'Actor photo',
  TRAILER_VIDEO: 'Movie trailer video',
  POSTER_GALLERY: 'Movie poster gallery',

  // Modals
  CLOSE_MODAL: 'Close modal',
  CATEGORY_MODAL: 'Category modal',
  POSTER_MODAL: 'Poster modal',
  RECOMMENDATIONS_MODAL: 'Recommendations modal',
};
